import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { BookOpen, ArrowLeft, FileText, Users, Star, GraduationCap, BookMarked, ClipboardList, Brain, ScrollText, FlaskConical, Pencil } from 'lucide-react';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';

const typeIcons = {
  'Lecture Notes': ScrollText,
  'Study Guide': BookMarked,
  'Cheat Sheet': ClipboardList,
  'Practice Exam': FlaskConical,
  'Flashcards': Brain,
  'Research Paper': FileText,
  'Other': FileText,
};

const CourseHub = () => {
  const { slug } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [sellers, setSellers] = useState([]);
  const [purchasedIds, setPurchasedIds] = useState([]);
  const [studyMaterialIds, setStudyMaterialIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [renamingDoc, setRenamingDoc] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  useEffect(() => {
    fetchCourseHub();
  }, [slug]);

  const fetchCourseHub = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/courses/${slug}`);
      setCourse(response.data.course);
      setDocuments(response.data.documents || []);
      setGrouped(response.data.grouped || {});
      setSellers(response.data.sellers || []);
      setPurchasedIds(response.data.purchased_ids || []);
      setStudyMaterialIds(response.data.study_material_ids || []);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Course not found');
        navigate('/courses');
      } else {
        toast.error('Failed to load course');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRename = async (docId) => {
    const trimmed = renameValue.trim();
    if (!trimmed) { setRenamingDoc(null); return; }
    try {
      await axios.put(`${API}/documents/${docId}`, { title: trimmed });
      setDocuments(prev => prev.map(d => d.id === docId ? { ...d, title: trimmed } : d));
      // Also update grouped
      setGrouped(prev => {
        const updated = {};
        for (const [key, docs] of Object.entries(prev)) {
          updated[key] = docs.map(d => d.id === docId ? { ...d, title: trimmed } : d);
        }
        return updated;
      });
      toast.success('Title updated');
    } catch (e) {
      toast.error('Failed to rename');
    }
    setRenamingDoc(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-stone-50 via-blue-50 to-stone-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!course) return null;

  const docTypes = Object.keys(grouped);
  const examDocs = grouped['Practice Exam'] || [];

  const DocCard = ({ doc }) => {
    const isPurchased = purchasedIds.includes(doc.id);
    const hasStudy = studyMaterialIds.includes(doc.id);
    const isOwner = user && doc.seller_id === user.id;
    const isRenaming = renamingDoc === doc.id;

    return (
      <div
        data-testid={`course-doc-${doc.id}`}
        className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 overflow-hidden group transition-all duration-200 hover:-translate-y-0.5"
      >
        <Link to={`/document/${doc.id}`} className="block p-4 pb-0">
          <div className="flex items-start justify-between mb-2">
            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-md">
              {doc.document_type || 'Notes'}
            </span>
            <span className="text-lg font-bold text-blue-800" style={{ fontFamily: 'Crimson Text, serif' }}>
              ${typeof doc.price === 'number' ? doc.price.toFixed(2) : parseFloat(doc.price || 0).toFixed(2)}
            </span>
          </div>
        </Link>
        <div className="px-4 pb-0">
          {isRenaming ? (
            <Input
              data-testid={`course-rename-input-${doc.id}`}
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onBlur={() => handleRename(doc.id)}
              onKeyDown={e => { if (e.key === 'Enter') handleRename(doc.id); if (e.key === 'Escape') setRenamingDoc(null); }}
              autoFocus
              className="rounded-lg text-sm font-semibold h-8 mb-1.5"
            />
          ) : (
            <div className="flex items-center gap-1.5 group/title mb-1.5">
              <Link to={`/document/${doc.id}`} className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-700 transition-colors" style={{ fontFamily: 'Crimson Text, serif' }}>
                  {doc.title}
                </h3>
              </Link>
              {isOwner && (
                <button
                  data-testid={`course-rename-btn-${doc.id}`}
                  onClick={() => { setRenamingDoc(doc.id); setRenameValue(doc.title); }}
                  className="text-gray-300 hover:text-blue-600 transition-colors opacity-0 group-hover/title:opacity-100 flex-shrink-0"
                  title="Rename"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
        <Link to={`/document/${doc.id}`} className="block px-4 pb-2">
          <p className="text-sm text-gray-500 line-clamp-2 mb-3">{doc.description}</p>
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-1.5">
              <Avatar className="w-5 h-5">
                <AvatarFallback className="bg-blue-100 text-blue-900 text-[10px]">
                  {doc.seller_name?.charAt(0)?.toUpperCase() || '?'}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs text-gray-600">{doc.seller_name}</span>
            </div>
            <div className="flex items-center gap-2">
              {isPurchased && (
                <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded-md">Purchased</span>
              )}
              {isOwner && (
                <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-md">Your doc</span>
              )}
              {doc.average_rating > 0 && (
                <div className="flex items-center gap-0.5">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  <span className="text-xs text-gray-600">{doc.average_rating.toFixed(1)}</span>
                </div>
              )}
            </div>
          </div>
        </Link>
        {/* Study button - visible when user has access and materials exist */}
        {hasStudy && (isOwner || isPurchased) && (
          <div className="px-4 pb-3">
            <Button
              data-testid={`study-btn-${doc.id}`}
              onClick={() => navigate(`/study/${doc.id}`)}
              size="sm"
              className="w-full rounded-lg bg-blue-600 hover:bg-blue-700"
            >
              <Brain className="w-4 h-4 mr-1.5" /> Study Dashboard
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-blue-50 to-stone-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-btn"
              onClick={() => navigate('/courses')}
              variant="ghost"
              size="icon"
              className="rounded-xl"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-blue-50" />
              </div>
              <span className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>DocMarket</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 md:py-10">
        {/* Course Header */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {course.code && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-mono font-bold rounded-lg">
                    {course.code}
                  </span>
                )}
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-lg">
                  {course.subject}
                </span>
              </div>
              <h1 data-testid="course-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
                {course.name}
              </h1>
              {course.school && (
                <p className="text-base text-gray-500">{course.school}</p>
              )}
            </div>
            <div className="flex gap-6 md:gap-8">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-700">{course.document_count}</p>
                <p className="text-xs text-gray-500">Documents</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-700">{course.seller_count}</p>
                <p className="text-xs text-gray-500">Contributors</p>
              </div>
            </div>
          </div>

          {/* Contributors */}
          {sellers.length > 0 && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <p className="text-sm font-medium text-gray-600 mb-2">Contributors</p>
              <div className="flex flex-wrap gap-2">
                {sellers.map((seller) => (
                  <Link
                    key={seller.id}
                    to={`/profile/${seller.id}`}
                    data-testid={`contributor-${seller.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-full hover:bg-blue-50 transition-colors border border-gray-200"
                  >
                    <Avatar className="w-5 h-5">
                      <AvatarFallback className="bg-blue-100 text-blue-900 text-[10px]">
                        {seller.name?.charAt(0)?.toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-gray-700">{seller.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Exam Pack Section */}
        {examDocs.length > 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-5 md:p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FlaskConical className="w-5 h-5 text-amber-700" />
              <h2 className="text-lg font-bold text-amber-900" style={{ fontFamily: 'Crimson Text, serif' }}>
                Exam Prep Pack
              </h2>
              <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs rounded-full ml-1">
                {examDocs.length} {examDocs.length === 1 ? 'resource' : 'resources'}
              </span>
            </div>
            <p className="text-sm text-amber-700 mb-4">Practice exams and exam guides for this course</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {examDocs.map((doc) => (
                <DocCard key={doc.id} doc={doc} />
              ))}
            </div>
          </div>
        )}

        {/* Document Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border border-gray-200 rounded-xl p-1 mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger
              value="all"
              data-testid="tab-all"
              className="rounded-lg text-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              All ({documents.length})
            </TabsTrigger>
            {docTypes.map((type) => {
              const IconComp = typeIcons[type] || FileText;
              return (
                <TabsTrigger
                  key={type}
                  value={type}
                  data-testid={`tab-${type.toLowerCase().replace(/\s+/g, '-')}`}
                  className="rounded-lg text-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                >
                  <IconComp className="w-3.5 h-3.5 mr-1" />
                  {type} ({grouped[type].length})
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="all">
            {documents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map((doc) => (
                  <DocCard key={doc.id} doc={doc} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No documents in this course yet</p>
              </div>
            )}
          </TabsContent>

          {docTypes.map((type) => (
            <TabsContent key={type} value={type}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {grouped[type].map((doc) => (
                  <DocCard key={doc.id} doc={doc} />
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
};

export default CourseHub;
